import React, { useEffect, useState } from "react";
import PageWrapper from "../components/PageWrapper";
import Colors from '../styles/Colors.json';
import { useSiteContext } from "../SiteContext";
import { getMiscellaneousTasksForPayPeriod, updateMiscellaneousTaskSuperDescription, denyMiscellaneousTask } from "../api/miscellaneousTasks";
import { FullMiscEmployeeTaskWithEmployee } from "../interfaces/MiscEmployeeTaskInterface";
import './EmployeeView.css';
import ToggleSwitch from "../components/ToggleSwitch";

const MiscellaneousTasksPage: React.FC = () => {
    const { payPeriodID, setLoading, createPopup, miscTaskApprovalConfigs, userRole, userID } = useSiteContext();
    const [tasks, setTasks] = useState<FullMiscEmployeeTaskWithEmployee[]>([]);
    const [editedDescriptions, setEditedDescriptions] = useState<Record<number, string>>({});
    const [showRequiresApproval, setShowRequiresApproval] = useState<boolean>(true);
    const [denyTaskID, setDenyTaskID] = useState<number | null>(null);
    const [denyReason, setDenyReason] = useState('');
    const [isDenying, setIsDenying] = useState(false);

    // Fetch miscellaneous tasks when pay period changes
    useEffect(() => {
        const fetchTasks = async () => {
            if (!payPeriodID || payPeriodID === 0) {
                setTasks([]);
                return;
            }

            setLoading(true);
            try {
                const data = await getMiscellaneousTasksForPayPeriod(payPeriodID);
                setTasks(data || []);
                // Initialize edited descriptions with current values
                const initialDescriptions: Record<number, string> = {};
                (data || []).forEach(task => {
                    initialDescriptions[task.miscEmployeeTaskID] = task.superDescription || '';
                });
                setEditedDescriptions(initialDescriptions);
            } catch (error) {
                console.error("Error fetching miscellaneous tasks:", error);
                setTasks([]);
                setEditedDescriptions({});
            } finally {
                setLoading(false);
            }
        };

        fetchTasks();
    }, [payPeriodID, setLoading]);

    // Format employee name
    const formatEmployeeName = (name: { first: string; middle: string; last: string }) => {
        return `${name.first} ${name.last}`;
    };

    // Handle super description change
    const handleDescriptionChange = (taskID: number, value: string) => {
        if (value.length <= 60) {
            setEditedDescriptions(prev => ({
                ...prev,
                [taskID]: value
            }));
        }
    };

    // Handle save button click
    const handleSave = async (taskID: number) => {
        const description = editedDescriptions[taskID] ?? '';

        if (description.trim().length === 0) {
            createPopup('Super Description Required', 'A Super Description is required to approve this task.');
            return;
        }

        setLoading(true);
        try {
            const response = await updateMiscellaneousTaskSuperDescription(taskID, description);
            
            if (response.success) {
                // Update the task in the state with the new description
                setTasks(prevTasks => 
                    prevTasks.map(task => 
                        task.miscEmployeeTaskID === taskID 
                            ? { ...task, superDescription: description }
                            : task
                    )
                );
                createPopup('Success', 'Updated successfully.');
            } else {
                // Show error message from response
                createPopup('Update Failed', response.message || 'Failed to update.');
            }
        } catch (error) {
            console.error('Error saving super description:', error);
            createPopup('Update Failed', 'An error occurred while updating.');
        } finally {
            setLoading(false);
        }
    };

    const handleDenyConfirm = async () => {
        if (denyTaskID === null || !userID) return;
        setIsDenying(true);
        try {
            const response = await denyMiscellaneousTask(denyTaskID, userID, denyReason);
            if (response.success) {
                setTasks(prev => prev.filter(t => t.miscEmployeeTaskID !== denyTaskID));
                setDenyTaskID(null);
                setDenyReason('');
                createPopup('Success', 'Task denied successfully.');
            } else {
                createPopup('Deny Failed', response.message || 'Failed to deny task.');
            }
        } catch (error) {
            createPopup('Deny Failed', 'An error occurred while denying the task.');
        } finally {
            setIsDenying(false);
        }
    };

    // Filter tasks based on approval status and config (if supervisor with config)
    const filteredTasks = tasks.filter(task => {
        // First, apply config-based filtering if user is supervisor with configs
        if (userRole.supervisor && miscTaskApprovalConfigs && miscTaskApprovalConfigs.length > 0) {
            // Check if task matches any of the configs
            const matchesConfig = miscTaskApprovalConfigs.some(config => {
                // Check locationID match
                const locationMatches = task.locationID === config.locationID;
                
                // Check employeeDepartment prefix match
                const departmentMatches = task.employeeDepartment 
                    ? task.employeeDepartment.startsWith(config.employeeDepartmentPrefix)
                    : false;
                
                return locationMatches && departmentMatches;
            });
            
            // If task doesn't match any config, filter it out
            if (!matchesConfig) {
                return false;
            }
        }
        
        // Then apply approval status filter (only check saved superDescription, not edited values)
        const hasDescription = (task.superDescription || '').trim().length > 0;
        return showRequiresApproval ? !hasDescription : hasDescription;
    });

    return (
        <PageWrapper title="Miscellaneous Tasks" backgroundColor={Colors.White} weekSelector={true}>
            {payPeriodID === 0 ? (
                <p>Please select a pay period to view miscellaneous tasks.</p>
            ) : tasks.length === 0 ? (
                <p>No miscellaneous tasks found for the selected pay period.</p>
            ) : (
                <>
                    <div className="employee-view-top-row" style={{ marginBottom: '20px', justifyContent: 'flex-start' }}>
                        <ToggleSwitch
                            label="Requires Approval"
                            checked={showRequiresApproval}
                            onChange={(checked) => setShowRequiresApproval(checked)}
                        />
                    </div>
                    <div className="employee-view-table">
                        <table>
                            <thead>
                                <tr className="employee-view-table-row-blue">
                                    <th>Employee</th>
                                    <th>ID #</th>
                                    <th>Supervisor</th>
                                    <th>Project</th>
                                    <th>Location / Phase</th>
                                    <th>Amount</th>
                                    <th>Misc Description</th>
                                    <th style={{ minWidth: '300px' }}>Super Description</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTasks.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ textAlign: 'center', padding: '20px' }}>
                                            No {showRequiresApproval ? 'tasks requiring approval' : 'approved tasks'} found for the selected pay period.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTasks.map((task, index) => (
                                <tr 
                                    key={task.miscEmployeeTaskID} 
                                    className={index % 2 === 1 ? "employee-view-table-row-blue" : "employee-view-table-row-white"}
                                >
                                    <td>{formatEmployeeName(task.name)}</td>
                                    <td>{task.altEmployeeID}</td>
                                    <td>{task.supervisor ? formatEmployeeName(task.supervisor.name) : '-'}</td>
                                    <td>{task.project?.name || '-'} ({task.project?.altProjectID || '-'})</td>
                                    <td>{task.intermediateName ? `${task.intermediateName}` : '-'}</td>
                                    <td style={{ textAlign: 'right' }}>${task.amountClaimed.toFixed(2)}</td>
                                    <td>{task.miscDescription || '-'}</td>
                                    <td style={{ padding: '10px 20px' }}>
                                        <textarea
                                            value={task.miscEmployeeTaskID in editedDescriptions 
                                                ? editedDescriptions[task.miscEmployeeTaskID] 
                                                : (task.superDescription || '')}
                                            onChange={(e) => handleDescriptionChange(task.miscEmployeeTaskID, e.target.value)}
                                            maxLength={60}
                                            rows={3}
                                            style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ccc',
                                                borderRadius: '4px',
                                                fontFamily: 'inherit',
                                                fontSize: 'inherit',
                                                resize: 'vertical',
                                                boxSizing: 'border-box'
                                            }}
                                        />
                                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                            {(task.miscEmployeeTaskID in editedDescriptions 
                                                ? editedDescriptions[task.miscEmployeeTaskID] 
                                                : (task.superDescription || '')).length}/60
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                                            <button
                                                onClick={() => handleSave(task.miscEmployeeTaskID)}
                                                style={{ padding: '8px 16px', backgroundColor: '#276EF1', color: 'white',
                                                         border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = '#1e5cd4'; }}
                                                onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = '#276EF1'; }}
                                            >
                                                Approve
                                            </button>
                                            <button
                                                onClick={() => { setDenyTaskID(task.miscEmployeeTaskID); setDenyReason(''); }}
                                                style={{ padding: '8px 16px', backgroundColor: Colors.White, color: Colors.Red,
                                                         border: `1px solid ${Colors.Red}`, borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
                                                onMouseOver={(e) => { e.currentTarget.style.backgroundColor = Colors.RedTransparent70; e.currentTarget.style.color = 'white'; }}
                                                onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = Colors.White; e.currentTarget.style.color = Colors.Red; }}
                                            >
                                                Deny
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
            {denyTaskID !== null && (
                <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200 }}>
                    <div style={{ backgroundColor: Colors.White, borderRadius: '8px',
                                  boxShadow: '0 10px 30px rgba(0,0,0,0.25)', width: '420px',
                                  maxWidth: '90vw', padding: '24px' }}>
                        <h3 style={{ margin: '0 0 12px 0' }}>Deny Miscellaneous Task</h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '14px' }}>
                            Are you sure you want to deny this miscellaneous task? Please provide a reason.
                        </p>
                        <textarea
                            value={denyReason}
                            onChange={(e) => { if (e.target.value.length <= 60) setDenyReason(e.target.value); }}
                            maxLength={60}
                            rows={3}
                            placeholder="Reason for denial (required)"
                            style={{ width: '100%', padding: '8px', border: '1px solid #ccc',
                                     borderRadius: '4px', fontFamily: 'inherit', fontSize: 'inherit',
                                     resize: 'vertical', boxSizing: 'border-box' }}
                        />
                        <div style={{ fontSize: '12px', color: '#666', textAlign: 'right', marginBottom: '16px' }}>
                            {denyReason.length}/60
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button
                                onClick={() => { setDenyTaskID(null); setDenyReason(''); }}
                                disabled={isDenying}
                                style={{ padding: '8px 16px', backgroundColor: Colors.ButtonSecondary,
                                         color: '#333', border: 'none', borderRadius: '4px',
                                         cursor: 'pointer', fontSize: '14px' }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDenyConfirm}
                                disabled={isDenying || denyReason.trim().length === 0}
                                style={{ padding: '8px 16px', backgroundColor: Colors.Red, color: 'white',
                                         border: 'none', borderRadius: '4px',
                                         cursor: (isDenying || denyReason.trim().length === 0) ? 'not-allowed' : 'pointer',
                                         opacity: (isDenying || denyReason.trim().length === 0) ? 0.5 : 1,
                                         fontSize: '14px' }}
                            >
                                {isDenying ? 'Denying...' : 'Confirm Deny'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PageWrapper>
    );
};

export default MiscellaneousTasksPage;

